
import AttachmentOutlinedIcon from '@mui/icons-material/AttachmentOutlined';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from "@mui/icons-material/Send";
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import { Avatar, Box, Button, CircularProgress, IconButton, MenuItem, Skeleton, TextField, Typography } from "@mui/material";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Conversation,
  Media,
  Message,
  Paginator,
  Client as TwilioClient,
} from "@twilio/conversations";
import AllAvailabilitiesPopup from 'app/components/Chat/AllAvailabilitiesPopup';
import ConfirmShareDialog from 'app/components/Chat/ConfirmShareDialog';
import { generateTimeOptions, QuickLinkForm, QuickLinkFormValues } from 'app/components/Chat/QuickLinkForm';
import Label from 'app/components/NewItemForm/components/Label';
import EmojiPicker from 'emoji-picker-react';
import { Field, Formik } from 'formik';
import { baseWebUrl } from 'lib/constants';
import { useCreateAvailabilityMutation } from "lib/features/availability/availabilityService";
import { AvailabilityDTO } from "lib/features/availability/types";
import { Business } from "lib/features/business/types";
import { useCreateItemMutation } from "lib/features/item/itemService";
import { CreateItemWithDetailDto } from "lib/features/item/types";
import { setCurrentChannelUrl, setIsExistingBookingLink } from "lib/features/message/messageSlice";
import { useCreateReferralMutation, useLazyGetReferralByIdQuery } from "lib/features/referral/referralService";
import { selectUser } from "lib/features/user/userSlice";
import { useAppDispatch, useAppSelector } from "lib/hooks";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import businessServices from "services/business-service";
import { messageStyles } from "styles/ChatDirectory/MessageView.styles";
import { checkoutTime } from "util/commonHelper";
import { getItemImageSource } from "util/getImageSource";
import { parseImageUrl } from "util/parseImageUrl";
import * as Yup from "yup";
import CalendarIcon from '../../assets/icons/calendar_icon.svg';
import LinkIcon from '../../assets/icons/link_chain.png';
const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

type Props = {
  conversationSid: string;
  getClient: () => Promise<TwilioClient>; // must resolve to a connected client
  currentUserId: string;
  avatar: string; // other participant avatar
  name: string;   // other participant display name
};

type ConfirmShareFlowType = 'quick_link' | 'create_availability';

type ReferralMessageProps = {
  body: string;
  isMine: boolean;
};

function ReferralMessage({ body, isMine }: ReferralMessageProps) {
  const navigate = useNavigate();
  const [getReferralById, { data: referralData, isLoading: isReferralLoading }] =
    useLazyGetReferralByIdQuery();
  const containsReferralLink = (text: string): boolean => {
    const referralPattern = /https:\/\/app\.refereree\.com\/checkout\/\d+/;
    return referralPattern.test(text);
  };

  const extractIdFromMessage = (text: string): string | null => {
    const match = text.match(/https:\/\/app\.refereree\.com\/checkout\/(\d+)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    if (!body || !containsReferralLink(body)) return;
    const referralId = extractIdFromMessage(body);
    if (!referralId) return;
    getReferralById(Number(referralId));
  }, [body, getReferralById]);
  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: isMine ? "flex-end" : "flex-start", mb: 1, cursor: "pointer" }}>
        {isReferralLoading || !referralData ? (
          <Skeleton
            variant="rectangular"
            width={300}
            height={150}
            sx={messageStyles.messageSkeleton(isMine)}
          />
        ) : (
          <Box
            onClick={() => window.open(`/checkout/${referralData?.id}`, '_blank')}
            sx={messageStyles.imageMessage(false, isMine)}>
            <Box sx={{ alignItems: "center", justifyContent: "center" }}>
              <img
                src={getItemImageSource(referralData?.item)}
                style={messageStyles.image}
              />
            </Box>
            <Typography sx={messageStyles.itemDetailsText}>
              {referralData?.item?.item_type === 4
                ? referralData?.item.farehabor_item_details?.name
                : referralData?.item?.itemDetails[0]?.item_name}
              <br />
              {referralData?.item?.item_type === 4
                ? moment(referralData?.selected_farehabor_availability?.start_at).format("MM/DD/YY")
                : moment(referralData?.availability?.start_time).format("MM/DD/YY")},&nbsp;
              {referralData?.item?.item_type === 4
                ? checkoutTime(referralData?.selected_farehabor_availability?.start_at)
                : checkoutTime(referralData?.availability?.start_time)} -
              {referralData?.item?.item_type === 4
                ? checkoutTime(referralData?.selected_farehabor_availability?.end_at)
                : checkoutTime(referralData?.availability?.end_time)}
            </Typography>
            <Typography sx={messageStyles.appText}>
              app.refereree.com
            </Typography>
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 0.25, textAlign: isMine ? "right" : "left" }}>
        {referralData ? `${moment(referralData?.created_time).format("MM/DD/YYYY")}, ${checkoutTime(referralData.created_time)}` : ""}
      </Typography>
    </Box>
  );
}

const MediaMessage = ({ media }: { media: Media }) => {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    media.getContentTemporaryUrl().then((url: any) => {
      setMediaUrl(url);
      setLoading(false);
    }).catch((err: Error) => {
      console.error("Error fetching media url", err);
      setLoading(false);
    });
  }, [media]);

  if (loading) return <CircularProgress size={20} />;
  if (!mediaUrl) return null;

  const isImage = media.contentType.startsWith("image/");

  if (isImage) {
    return (
      <img
        src={mediaUrl}
        alt={media.filename || "Image"}
        style={{ width: "100%", maxHeight: 400, borderRadius: 8, cursor: 'pointer', display: 'block' }}
        onClick={() => window.open(mediaUrl, "_blank")}
      />
    );
  }

  return (
    <Box sx={{ mt: 1, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
      <AttachmentOutlinedIcon />
      <a href={mediaUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
        <Typography variant="body2" sx={{ textDecoration: 'underline' }}>
          {media.filename || "Attachment"}
        </Typography>
      </a>
    </Box>
  );
};

export default function ConversationView({
  conversationSid,
  getClient,
  currentUserId,
  avatar,
  name,
}: Props) {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(selectUser);
  const [businessData, setBusinessData] = useState<Business[]>();
  const [convo, setConvo] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [olderPage, setOlderPage] = useState<Paginator<Message> | null>(null);
  const [shareLinkDialog, setShareLinkDialog] = useState<boolean>(false);
  const [quickLinkDialog, setQuickLinkDialog] = useState<boolean>(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [quickLinkSharing, setQuickLinkSharing] = useState(false);
  const [createAvailabilityPopup, setCreateAvailabilityPopup] = useState<boolean>(false)
  const [addAvailabilityDialog, setAddAvailabilityDialog] = useState<boolean>(false)
  const scrollRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const pathname = window.location.pathname; // e.g. "/chat/123/456"
  const [createQuickLinkFormValues, setCreateQuickLinkFormValues] = useState<QuickLinkFormValues>({
    item_name: "",
    date: null,
    start_time: generateTimeOptions()[24],
    quantity: 1,
    duration_days: null,
    duration_hours: null,
    duration_minutes: null,
    description: "",
    whats_included: "",
    cancellation_policy: "",
    price: 0,
    commission: 0,
    customer_added_value: "",
    itemImages: []
  })
  const [confirmShareDialog, setConfirmShareDialog] = useState<boolean>(false);
  const [confirmShareFlowType, setConfirmShareFlowType] = useState<ConfirmShareFlowType | null>(null);
  const [createItem] = useCreateItemMutation();
  const [createAvailability] = useCreateAvailabilityMutation();
  const [createReferral] = useCreateReferralMutation();
  const [confirmShareData, setConfirmShareData] = useState<any>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [firstTimeQuantityClick, setFirstTimeQuantityClick] = useState<boolean>(true);

  const parts = pathname.split("/").filter(Boolean);
  const lastTwo = parts.slice(-3)
  avatar = decodeURIComponent(lastTwo[0] || "");
  name = decodeURIComponent(lastTwo[1] || "");
  let myavatar = decodeURIComponent(lastTwo[2] || "");
  // keep a stable reference to the getClient function so the effect
  // doesn't rerun on every render
  const getClientRef = useRef(getClient);
  useEffect(() => {
    getClientRef.current = getClient;
  }, [getClient]);

  useEffect(() => {
    const fetchBusiness = async () => {
      if (!user?.id) return;
      try {
        const response = await businessServices.getUserBusiness(user.id);
        setBusinessData(response?.data);
      } catch (err) {
        console.log("error fetching business in ConversationView");
      }
    };

    fetchBusiness();
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    let conversation: Conversation | null = null;

    const onAdded = (m: Message) => {
      setMessages((prev) => [...prev, m]);
      setTimeout(scrollToBottom, 0);
    };
    const onUpdated = ({ message }: { message: Message }) =>
      setMessages((prev) =>
        prev.map((x) => (x.sid === message.sid ? message : x))
      );
    const onRemoved = (m: Message) =>
      setMessages((prev) => prev.filter((x) => x.sid !== m.sid));

    (async () => {
      const client = await getClientRef.current();
      conversation = await client.getConversationBySid(conversationSid);
      if (cancelled) return;

      setConvo(conversation);

      const page = await conversation.getMessages(50);
      if (cancelled) return;

      setMessages(page.items);
      setOlderPage(page.hasPrevPage ? page : null);

      conversation.removeAllListeners("messageAdded");
      conversation.removeAllListeners("messageUpdated");
      conversation.removeAllListeners("messageRemoved");

      conversation.on("messageAdded", onAdded);
      conversation.on("messageUpdated", onUpdated);
      conversation.on("messageRemoved", onRemoved);

      setTimeout(scrollToBottom, 0);
    })();

    return () => {
      cancelled = true;
      if (conversation) {
        conversation.off("messageAdded", onAdded);
        conversation.off("messageUpdated", onUpdated);
        conversation.off("messageRemoved", onRemoved);
      }
    };
  }, [conversationSid]);

  const onTopReached = async () => {
    if (!olderPage) return;
    const prev = await olderPage.prevPage();
    setMessages((prevMsgs) => [...prev.items, ...prevMsgs]);
    setOlderPage(prev.hasPrevPage ? prev : null);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  const handleSend = async () => {
    const text = input.trim();

    // Handle file attachment
    if (pendingFile && convo) {
      try {
        setSending(true);
        const messageBuilder = convo.prepareMessage();

        // Add media attachment
        messageBuilder.addMedia({
          contentType: pendingFile.type,
          filename: pendingFile.name,
          media: pendingFile,
        });

        // Add text body if present
        if (text) {
          messageBuilder.setBody(text);
        }

        await messageBuilder.build().send();
        setInput("");
        clearAttachment();
        setTimeout(scrollToBottom, 0);
      } catch (error) {
        console.error('Error sending file:', error);
      } finally {
        setSending(false);
      }
      return;
    }

    // Handle text-only message
    if (!text || !convo) return;
    try {
      setSending(true);
      await convo.sendMessage(text);
      setInput("");
      setTimeout(scrollToBottom, 0);
    } finally {
      setSending(false);
    }
  };

  const clearAttachment = () => {
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
    }
    setPendingFile(null);
    setFilePreviewUrl(null);
  };

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const items = useMemo(() => messages, [messages]);

  const handleShareLinkDialogOpen = () => {
    setShareLinkDialog(true);
  };

  const handleShareLinkDialogClose = () => {
    setShareLinkDialog(false);
  };

  const handleCreateAvailabilityPopupOpen = () => {
    setCreateAvailabilityPopup(true);
  };

  const handleCreateAvailabilityPopupClose = () => {
    setCreateAvailabilityPopup(false);
  };

  const handleAddAvailabilityDialogOpen = () => {
    setAddAvailabilityDialog(true);
  };

  const handleAddAvailabilityDialogClose = () => {
    setAddAvailabilityDialog(false);
  };

  const handleQuickLinkDialogOpen = () => {
    setQuickLinkDialog(true);
  };

  const handleQuickLinkDialogClose = () => {
    setQuickLinkDialog(false);
  };
  const handleQuickLinkSubmit = () => {
    setConfirmShareData(createQuickLinkFormValues);
    setConfirmShareFlowType('quick_link');
    setConfirmShareDialog(true);
  };


  const handleConfirmShareDialogClose = () => {
    setConfirmShareDialog(false);
  };
  const handleConfirmShareDialogOpen = () => {
    setConfirmShareDialog(true);
  };

  const handleConfirmShareAndSend = async () => {
    try {
      setQuickLinkSharing(true);
      if (!user?.id || !businessData?.[0]?.id || !convo) {
        console.log("Missing user, business, or conversation");
        return;
      }

      const businessId = businessData[0].id;
      let itemId: number;
      let availabilityId: number;
      const duration = (createQuickLinkFormValues.duration_hours || (createQuickLinkFormValues.duration_days && createQuickLinkFormValues.duration_days * 24) || '') + '.' + (createQuickLinkFormValues.duration_minutes || '00');
      if (confirmShareFlowType === 'quick_link') {
        // QUICK LINK FLOW: Create both item and availability
        const itemPayload: Partial<CreateItemWithDetailDto> = {
          businessId,
          item_type: 1,
          item_name: createQuickLinkFormValues.item_name,
          unit_name: "ticket",
          headline: createQuickLinkFormValues.description,
          description: createQuickLinkFormValues.description,
          price: createQuickLinkFormValues.price,
          comission: createQuickLinkFormValues.commission,
          capacity: createQuickLinkFormValues.quantity || 0,
          cancellation_policy: createQuickLinkFormValues.cancellation_policy,
          whats_included: createQuickLinkFormValues.whats_included,
          images: createQuickLinkFormValues.itemImages,
          duration_applicable: (createQuickLinkFormValues.duration_days || createQuickLinkFormValues.duration_hours || createQuickLinkFormValues.duration_minutes) ? true : false,
          duration: duration,
          industry_ids: [],
          category_ids: [],
        };

        const createdItem = await createItem(itemPayload).unwrap();
        itemId = createdItem?.id;

        if (!itemId) {
          console.log("Item creation did not return an id", createdItem);
          return;
        }

        if (!createQuickLinkFormValues.date) {
          console.log("Date is required to create availability for quick link");
          return;
        }

        const [month, day, year] = moment(
          createQuickLinkFormValues.date,
          "YYYY-MM-DD"
        )
          .format("MM-DD-YYYY")
          .split("-");

        const timeString = createQuickLinkFormValues.start_time || "09:00 AM";
        const timeMoment = moment(timeString, ["h:mm A", "HH:mm"]);

        const startDateTime = moment(
          `${month}-${day}-${year} ${timeMoment.format("HH:mm")}`,
          "MM-DD-YYYY HH:mm"
        ).toDate();

        let endDateTime: Date;
        const { duration_days, duration_hours, duration_minutes } = confirmShareData;

        if (duration_days && duration_days > 0) {
          // If days are set, use days
          endDateTime = moment(startDateTime).add(duration_days, "days").toDate();
        } else if ((duration_hours && duration_hours > 0) || (duration_minutes && duration_minutes > 0)) {
          // If hours/minutes are set, use those
          endDateTime = moment(startDateTime)
            .add(duration_hours || 0, "hours")
            .add(duration_minutes || 0, "minutes")
            .toDate();
        } else {
          // Default: 2 hours
          endDateTime = moment(startDateTime).add(2, "hours").toDate();
        }

        const availability: AvailabilityDTO = {
          item_id: itemId,
          start_time: startDateTime,
          end_time: endDateTime,
          private: true,
          quantity: createQuickLinkFormValues.quantity || 0,
        };

        const availabilityResponse = await createAvailability([availability]).unwrap();
        const createdAvailability = availabilityResponse?.data?.[0] || availabilityResponse?.[0];
        availabilityId = createdAvailability?.id;

        if (!availabilityId) {
          console.log("Availability creation did not return an id", availabilityResponse);
          return;
        }

      } else if (confirmShareFlowType === 'create_availability') {
        // CREATE AVAILABILITY FLOW: Item already selected, only create availability
        itemId = confirmShareData?.id;

        if (!itemId) {
          console.log("No item selected for create availability flow");
          return;
        }

        if (!confirmShareData?.date) {
          console.log("Date is required to create availability");
          return;
        }

        const [month, day, year] = moment(
          confirmShareData.date,
          "YYYY-MM-DD"
        )
          .format("MM-DD-YYYY")
          .split("-");

        const timeString = confirmShareData.start_time || "09:00 AM";
        const timeMoment = moment(timeString, ["h:mm A", "HH:mm"]);

        const startDateTime = moment(
          `${month}-${day}-${year} ${timeMoment.format("HH:mm")}`,
          "MM-DD-YYYY HH:mm"
        ).toDate();

        const endDateTime = moment(startDateTime).add(2, "hours").toDate();

        const availability: AvailabilityDTO = {
          item_id: itemId,
          start_time: startDateTime,
          end_time: endDateTime,
          private: true,
          quantity: confirmShareData.quantity || 0,
        };

        const availabilityResponse = await createAvailability([availability]).unwrap();
        const createdAvailability = availabilityResponse?.data?.[0] || availabilityResponse?.[0];
        availabilityId = createdAvailability?.id;

        if (!availabilityId) {
          console.log("Availability creation did not return an id", availabilityResponse);
          return;
        }
      } else {
        console.log("Unknown flow type");
        return;
      }

      // Common: Create referral and send message
      const referralPayload = {
        user_id: user.id,
        business_id: businessId,
        item_id: itemId,
        availability_id: availabilityId,
        customer_name: name,
        referral_type: 1,
        state: 1,
      };

      const referral = await createReferral(referralPayload).unwrap();
      const url = `${baseWebUrl}/checkout/${referral?.id}`;

      await convo.sendMessage(url);

      // Reset state
      setCreateQuickLinkFormValues({
        item_name: "",
        date: null,
        start_time: generateTimeOptions()[24],
        quantity: 1,
        duration_days: null,
        duration_hours: null,
        duration_minutes: null,
        description: "",
        whats_included: "",
        cancellation_policy: "",
        price: 0,
        commission: 0,
        customer_added_value: "",
        itemImages: [],
      });
      setConfirmShareData(null);
      setConfirmShareFlowType(null);
      setShareLinkDialog(false);
      setQuickLinkDialog(false);
      setCreateAvailabilityPopup(false);
      setAddAvailabilityDialog(false);
      setConfirmShareDialog(false);
    } catch (error) {
      console.log("Error while creating and sending to chat", error);
    } finally {
      setQuickLinkSharing(false);
    }
  };


  const handleEmojiClick = (emoji: any) => {
    const message = input;
    setInput(message + emoji?.emoji);
    setShowEmojiPicker(false);
  };

  const handleCameraClick = () => {
    imageInputRef.current?.click();
  };

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !convo) return;

    // Store the file for preview (will be sent when user clicks send)
    setPendingFile(file);

    // Create preview URL for images
    if (file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setFilePreviewUrl(previewUrl);
    } else {
      setFilePreviewUrl(null);
    }

    // Reset input so the same file can be selected again
    e.target.value = '';
  };


  return (
    <Box
      sx={{
        height: "100%",
        display: "grid",
        gridTemplateRows: "56px 1fr 64px",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
        }}
      >
        <Typography sx={{ fontWeight: 700 }}>
          {convo?.friendlyName || name || `Conversation`}
        </Typography>
      </Box>

      {/* Messages */}
      <Box
        ref={scrollRef}
        onScroll={(e) => {
          const target = e.currentTarget;
          if (target.scrollTop <= 0) onTopReached();
        }}
        sx={{ overflowY: "auto", p: 2, mb: 8, display: "flex", flexDirection: "column" }}
      >
        <Box sx={{ marginTop: "auto" }}>
          {items.map((m) => {
            const isMine = String(m.author) === String(currentUserId);
            const body = (m as any).body as string | undefined;
            // Fallback: plain text bubble
            const displayName = isMine ? "You" : name || (m.author as string);
            if (body && body.includes("https://app.refereree.com/checkout/")) {

              return <Box
                sx={{
                  display: "flex",
                  flexDirection: isMine ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  gap: 1,
                }}>
                <Avatar
                  src={isMine ? parseImageUrl(myavatar) : parseImageUrl(avatar)}
                  alt={displayName}
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: 12,
                  }}
                >
                  {displayName.substring(0, 1).toUpperCase()}
                </Avatar>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start" }}>
                  <Typography
                    sx={{
                      fontSize: 9,
                      color: "text.disabled",
                      mb: 0.25,
                      textAlign: isMine ? "right" : "left",
                      ml: isMine ? 0 : 0.5,
                      mr: isMine ? 0.5 : 0,
                    }}
                  >
                    {displayName}
                  </Typography>
                  <ReferralMessage key={m.sid} body={body} isMine={isMine} />
                </Box>
              </Box>
            }



            return (
              <Box
                key={m.sid}
                sx={{
                  display: "flex",
                  justifyContent: isMine ? "flex-end" : "flex-start",
                  mb: 1.25,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: isMine ? "row-reverse" : "row",
                    alignItems: "flex-start",
                    gap: 1,
                  }}
                >
                  {/* Avatar */}
                  <Avatar
                    src={isMine ? parseImageUrl(myavatar) : parseImageUrl(avatar)}
                    alt={displayName}
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: 12,
                    }}
                  >
                    {displayName.substring(0, 1).toUpperCase()}
                  </Avatar>
                  {/* Name + Bubble */}
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: isMine ? "flex-end" : "flex-start" }}>
                    {/* Name above bubble */}
                    <Typography
                      sx={{
                        fontSize: 9,
                        color: "text.disabled",
                        mb: 0.25,
                        textAlign: isMine ? "right" : "left",
                        ml: isMine ? 0 : 0.5,
                        mr: isMine ? 0.5 : 0,
                      }}
                    >
                      {displayName}
                    </Typography>

                    {/* Message bubble */}
                    <Box
                      sx={{
                        maxWidth: (m as any).type === 'media' ? "90%" : "70%",
                        minWidth: "fit-content",
                        p: (m as any).type === 'media' ? 0.5 : 1.5,
                        borderRadius: 2,
                        bgcolor: isMine ? "#DAF3D9" : "white",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                      }}
                    >
                      {(m as any).type === 'media' && (m as any).attachedMedia?.map((media: Media, i: number) => (
                        <MediaMessage key={i} media={media} />
                      ))}
                      {"body" in m && m.body && (
                        <Typography
                          sx={{
                            fontSize: 12,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {(m as any).body}
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          fontSize: 9,
                          color: "text.disabled",
                          mt: 0.5,
                          textAlign: "right",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {m.dateCreated?.toLocaleString?.() ?? ""}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* Confirm Share Dialog*/}
      <BootstrapDialog
        onClose={handleConfirmShareDialogClose}
        aria-labelledby="customized-name-dialog-title"
        open={confirmShareDialog}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiPaper-root': { maxWidth: 690, padding: "29px 39px" } }}
      >
        <DialogTitle
          sx={{ m: 0, px: 0, pb: 0, textAlign: "center", width: "100%" }}
          id="customized-name-dialog-title"
        >
          <Typography
            sx={{ fontSize: "22px", fontWeight: "600", fontFamily: "Poppins" }}
          >
            {
              confirmShareFlowType === "quick_link" ? "Share with " + name + "?" : "Share Availability"
            }
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleConfirmShareDialogClose}
          sx={(theme) => ({
            position: 'absolute',
            right: "39px",
            top: "29px",
            backgroundColor: "#E8E8EB",
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon sx={{ color: "#000" }} />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <ConfirmShareDialog data={confirmShareData} handleConfirmShareDialogClose={handleConfirmShareDialogClose} quickLinkSharing={quickLinkSharing} handleConfirmShareAndSend={handleConfirmShareAndSend} type={confirmShareFlowType} />
        </DialogContent>
      </BootstrapDialog>
      {/* Create Quick Link Dialog*/}
      <BootstrapDialog
        onClose={handleQuickLinkDialogClose}
        aria-labelledby="customized-quick-link-dialog-title"
        open={quickLinkDialog}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiPaper-root': { maxWidth: "850px", height: "825px", padding: "29px 0" } }}
      >
        <DialogTitle
          sx={{ m: 0, px: 0, pb: 0, textAlign: "center", width: "100%" }}
          id="customized-quick-link-dialog-title"
        >
          <Typography variant="h1" sx={{ textAlign: "center", fontSize: "32px", fontWeight: "700", fontFamily: "Poppins", mb: "8px" }}>
            Quick Booking Link
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleQuickLinkDialogClose}
          sx={(theme) => ({
            position: 'absolute',
            right: "39px",
            top: "29px",
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon sx={{ color: "#000" }} />
        </IconButton>
        <DialogContent >
          <QuickLinkForm createQuickLinkFormValues={createQuickLinkFormValues} setCreateQuickLinkFormValues={setCreateQuickLinkFormValues} onSubmit={handleQuickLinkSubmit} businessData={businessData} />
        </DialogContent>
      </BootstrapDialog>
      {/* Share Link Dialog */}
      {businessData?.[0]?.id && (
        <BootstrapDialog
          onClose={handleShareLinkDialogClose}
          aria-labelledby="customized-share-link-dialog-title"
          open={shareLinkDialog}
          sx={{ '& .MuiPaper-root': { padding: "24px" } }}
        >
          <DialogTitle
            sx={{ m: 0, px: 0, textAlign: "center", width: "100%" }}
            id="customized-share-link-dialog-title"
          >
            <Typography variant="h3" sx={{ fontSize: "22px", fontWeight: "600", fontFamily: "Poppins" }}>Share Booking Link</Typography>
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleShareLinkDialogClose}
            sx={(theme) => ({
              position: 'absolute',
              right: "24px",
              top: "24px",
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon sx={{ color: "#595959" }} />
          </IconButton>
          <DialogContent sx={{ display: "flex", gap: 2 }} >
            <Button
              sx={{
                width: "178px",
                height: "39px",
                fontSize: "15px",
                fontWeight: "600",
                letterSpacing: "1.8%",
                borderRadius: "8px",
                fontFamily: "Poppins",
              }}
              size="small"
              variant="contained"
              onClick={() => {
                dispatch(setIsExistingBookingLink(true));
                // Store full path so navigation back includes name/avatar info
                dispatch(setCurrentChannelUrl(`${conversationSid}/${encodeURIComponent(avatar)}/${encodeURIComponent(name)}/${encodeURIComponent(myavatar)}`))
                const businessId = businessData?.[0]?.id;
                if (businessId) {
                  navigate(`/availabilities/${businessId}`, { state: { fromChat: true } });
                } else {
                  navigate("/availabilities", { state: { fromChat: true } });
                }
              }}
            >
              Existing Availability
            </Button>
            <Button sx={{
              width: "178px",
              height: "39px",
              fontSize: "15px",
              fontWeight: "600",
              letterSpacing: "1.8%",
              borderRadius: "8px",
              fontFamily: "Poppins",
            }} size="small" variant="outlined" onClick={handleCreateAvailabilityPopupOpen}>Create Availability</Button>
            <Button sx={{
              width: "178px",
              height: "39px",
              fontSize: "15px",
              fontWeight: "600",
              letterSpacing: "1.8%",
              borderRadius: "8px",
              fontFamily: "Poppins",
            }} size="small" variant="outlined" onClick={handleQuickLinkDialogOpen}>Create Quick Link</Button>
          </DialogContent>
        </BootstrapDialog>
      )}

      {/*All Availabilitry Dialog */}
      <BootstrapDialog
        onClose={handleCreateAvailabilityPopupClose}
        aria-labelledby="customized-create-availability-title"
        open={createAvailabilityPopup}
        sx={{ '& .MuiPaper-root': { maxWidth: "1066px", minWidth: "550px", overflowX: "hidden" } }}
      >
        <DialogTitle
          sx={{ m: 0, pl: 7, width: "100%" }}
          id="customized-create-availability-title"
        >
          <Typography variant="h1" sx={{ fontSize: "32px", fontWeight: "700", fontFamily: "Poppins" }}>Select or Create Item</Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCreateAvailabilityPopupClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon sx={{ width: "36px", height: "36px", color: "#000" }} />
        </IconButton>
        <DialogContent sx={{ '& .MuiDialogContent-root': { display: "flex", gap: 2, overflowX: "hidden", padding: 0 } }}>
          <AllAvailabilitiesPopup handleAddAvailabilityDialogOpen={handleAddAvailabilityDialogOpen} setSelectedItem={setConfirmShareData} />
        </DialogContent>
      </BootstrapDialog>

      {/*Add Availability Dialog */}
      <BootstrapDialog
        onClose={handleAddAvailabilityDialogClose}
        aria-labelledby="customized-create-availability-title"
        open={addAvailabilityDialog}
        sx={{ '& .MuiPaper-root': { maxWidth: "694px", padding: "35px 40px 46px 40px" } }}
      >
        <DialogTitle
          sx={{ m: 0, px: 0, pb: 0, textAlign: "center", width: "100%" }}
          id="customized-create-availability-title"
        >
          <Typography variant="h1" sx={{ textAlign: "center", fontSize: "32px", fontWeight: "700", fontFamily: "Poppins", mb: "8px" }}>
            Create Availability
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleAddAvailabilityDialogClose}
          sx={(theme) => ({
            position: 'absolute',
            right: "39px",
            top: "29px",
            backgroundColor: "#E8E8EB",
            borderRadius: "50%",
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon sx={{ color: "#000" }} />
        </IconButton>
        <DialogContent sx={{ p: "24px 16px 32px 16px", maxWidth: "616px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "584px" }}>
            <Typography variant="subtitle1" sx={{ textAlign: "center", fontSize: "20px", fontWeight: "600", fontFamily: "Poppins", color: "#000" }}>
              Select Date and Time
            </Typography>
            {(() => {
              // Generate time options outside render props to avoid recalculating
              const timeOptions = (() => {
                const times: string[] = [];
                const totalMinutes = 24 * 60;
                // Start from 12:00 PM (720 minutes) and wrap around
                for (let i = 0; i < 48; i++) {
                  const minutes = (720 + i * 30) % totalMinutes;
                  const hours = Math.floor(minutes / 60);
                  const mins = minutes % 60;
                  const period = hours >= 12 ? "AM" : "PM";
                  const displayHour = hours % 12 === 0 ? 12 : hours % 12;
                  const displayMins = mins.toString().padStart(2, "0");
                  times.push(`${displayHour}:${displayMins} ${period}`);
                }
                return times;
              })();

              const defaultStartTime = timeOptions[24]; // Default to 12:00 AM (index 24)

              return (
                <Formik
                  initialValues={{
                    date: null as string | null,
                    start_time: defaultStartTime,
                    quantity: 1,
                  }}
                  validationSchema={Yup.object({
                    date: Yup.string().required("Date is required"),
                    start_time: Yup.string().required("Start time is required"),
                    quantity: Yup.number().min(1, "Quantity must be at least 1"),
                  })}
                  onSubmit={(values) => {
                    setConfirmShareData((prev: any) => ({ ...prev, ...values }));
                    setConfirmShareFlowType('create_availability');
                    handleAddAvailabilityDialogClose();
                    handleConfirmShareDialogOpen();
                  }}
                >
                  {({ values, errors, touched, setFieldValue, handleSubmit }) => {
                    return (
                      <form onSubmit={handleSubmit} style={{ width: "372px" }}>
                        {/* Date Field */}
                        <Box sx={{ mb: 2 }}>
                          <Label label="Date" required={true} size="17px" />
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              minDate={new Date()}
                              value={
                                values.date
                                  ? (() => {
                                    const d = new Date(values.date as string);
                                    return Number.isNaN(d.getTime()) ? null : d;
                                  })()
                                  : null
                              }
                              onChange={(newValue: Date | null) => {
                                const isValidDate =
                                  newValue instanceof Date &&
                                  !Number.isNaN(newValue.getTime());

                                const formattedDate = isValidDate
                                  ? `${newValue.getFullYear()}-${String(newValue.getMonth() + 1).padStart(2, '0')}-${String(newValue.getDate()).padStart(2, '0')}`
                                  : null;
                                setFieldValue("date", formattedDate);
                              }}
                              slots={{
                                openPickerIcon: () => <img src={CalendarIcon} alt="calendar" style={{ width: 16, height: 16 }} />,
                              }}
                              slotProps={{
                                field: {
                                  readOnly: true,
                                },
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  error: touched.date && Boolean(errors.date),
                                  placeholder: "",
                                  sx: { cursor: 'pointer' },
                                  onClick: () => setDatePickerOpen(true),
                                },
                                inputAdornment: {
                                  position: 'start',
                                },
                              }}
                              open={datePickerOpen}
                              onOpen={() => setDatePickerOpen(true)}
                              onClose={() => setDatePickerOpen(false)}
                            />
                          </LocalizationProvider>
                          {errors.date && touched.date && (
                            <Typography color="red" fontSize="12px">
                              {errors.date}
                            </Typography>
                          )}
                        </Box>

                        {/* Start Time and Quantity */}
                        <Box sx={{ display: "flex", gap: 2 }}>
                          {/* Start Time */}
                          <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                            <Label label="Start Time" required={true} size="17px" margin='0' />
                            <Field
                              as={TextField}
                              name="start_time"
                              fullWidth
                              select
                              size="small"
                              value={values.start_time || ""}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setFieldValue("start_time", e.target.value);
                              }}
                              error={touched.start_time && Boolean(errors.start_time)}
                              SelectProps={{
                                MenuProps: {
                                  PaperProps: {
                                    sx: {
                                      '& .MuiMenuItem-root:hover': {
                                        backgroundColor: '#328734',
                                        color: '#fff',
                                      },
                                    },
                                  },
                                },
                              }}
                            >
                              {timeOptions.map((time: string) => (
                                <MenuItem key={time} value={time}>
                                  {time}
                                </MenuItem>
                              ))}
                            </Field>
                            {errors.start_time && touched.start_time && (
                              <Typography color="red" fontSize="12px">
                                {errors.start_time}
                              </Typography>
                            )}
                          </Box>

                          {/* Quantity */}
                          <Box sx={{ display: "flex", flexDirection: "column", width: 160 }}>
                            <Label label="Quantity" required={true} size="17px" margin='0' />
                            <Field
                              as={TextField}
                              type="number"
                              name="quantity"
                              fullWidth
                              size="small"
                              value={values.quantity}
                              onFocus={() => {
                                if (values.quantity === 1 && firstTimeQuantityClick) {
                                  setFieldValue("quantity", "");
                                  setFirstTimeQuantityClick(false);
                                }
                              }}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const val = e.target.value === "" ? "" : Number(e.target.value);
                                setFieldValue("quantity", val);
                              }}
                              error={touched.quantity && Boolean(errors.quantity)}
                              inputProps={{ min: 1 }}
                            />
                            {errors.quantity && touched.quantity && (
                              <Typography color="red" fontSize="12px">
                                {errors.quantity}
                              </Typography>
                            )}
                          </Box>
                        </Box>

                        {/* Submit Button */}
                        <Box sx={{ display: "flex", justifyContent: "center", mt: "34px" }}>
                          <Button
                            type="submit"
                            variant="contained"
                            size="small"
                            sx={{ width: "100%", maxWidth: "358px", paddingY: "10px", fontSize: "16px", fontWeight: "600", fontFamily: "Poppins" }}
                          >
                            Create and Share Availability
                          </Button>
                        </Box>
                      </form>
                    );
                  }}
                </Formik>
              );
            })()}
          </Box>
        </DialogContent>
      </BootstrapDialog>


      {/* Input */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          width: "calc(100% - 400px)",
          p: 2,
          bgcolor: "transparent",
        }}>
        {/* Attachment Preview */}
        {pendingFile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              mr: 2,
              mb: 1,
              bgcolor: '#f5f5f5',
              borderRadius: 2,
              position: 'relative',
            }}
          >
            {filePreviewUrl ? (
              <img
                src={filePreviewUrl}
                alt="Preview"
                style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 8 }}
              />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachmentOutlinedIcon />
              </Box>
            )}
            <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {pendingFile.name}
            </Typography>
            <IconButton size="small" onClick={clearAttachment} sx={{ ml: 'auto' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {/* Input Container with rounded border */}
        <Box
          sx={{
            border: '2px solid #e0e0e0',
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            borderRadius: 3,
            bgcolor: "white",
            p: "16px",
            mr: 2,
          }}
        >
          {/* Text Input */}
          <TextField
            sx={{
              height: "26px",
              fontSize: "17px",
              width: "100%",
              '& .MuiInputBase-root': {
                fontSize: 14,
              },
            }}
            variant="standard"
            InputProps={{
              disableUnderline: true,
            }}
            size="small"
            placeholder="Write a message..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />

          {/* Bottom row - Icons and Send Button */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {/* Left side - Icons and Share Button */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <input
                type="file"
                ref={imageInputRef}
                style={{ display: 'none' }}
                accept="image/*"
                onChange={handleFileSelect}
              />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <IconButton size="small" onClick={handleCameraClick} disabled={sending} sx={{ color: '#666' }}>
                <CameraAltOutlinedIcon sx={{ width: "24px", height: "24px" }} />
              </IconButton>
              <IconButton size="small" onClick={handleAttachmentClick} disabled={sending} sx={{ color: '#666' }}>
                <AttachmentOutlinedIcon sx={{ transform: "rotate(90deg)", width: "24px", height: "24px" }} />
              </IconButton>
              <Box sx={{ position: 'relative', display: 'inline-block' }} ref={emojiPickerRef}>
                <IconButton size="small" onClick={() => setShowEmojiPicker(!showEmojiPicker)} sx={{ color: '#666' }}>
                  <SentimentSatisfiedAltOutlinedIcon sx={{ width: "24px", height: "24px" }} />
                </IconButton>
                {showEmojiPicker && (
                  <Box sx={{ position: 'absolute', bottom: '100%', left: 0, zIndex: 1000 }}>
                    <EmojiPicker style={{ boxShadow: '0 0 20px rgba(0, 0, 0, 0.25)' }} onEmojiClick={(emoji) => handleEmojiClick(emoji)} />
                  </Box>
                )}
              </Box>
              {businessData?.[0]?.id && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={handleShareLinkDialogOpen}
                  sx={{
                    ml: 1,
                    height: "31px",
                    borderColor: '#4CAF50',
                    color: '#4CAF50',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: "15px",
                    '&:hover': {
                      borderColor: '#388E3C',
                      bgcolor: 'rgba(76, 175, 80, 0.04)',
                    },
                  }}
                >
                  <img src={LinkIcon} style={{ marginRight: 0.5, width: "20px", height: "20px" }} /> Share Booking link
                </Button>
              )}
            </Box>

            {/* Right side - Send Button */}
            <IconButton
              onClick={handleSend}
              disabled={sending || (!input.trim() && !pendingFile)}
              aria-label="Send"
              sx={{
                color: sending || (!input.trim() && !pendingFile) ? '#ccc' : '#4CAF50',
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>

  );
}